import type { EntitlementItem, EntitlementNode } from "./types"

/**
 * Dev-only. The commerce half of the same dump `library/cardTelemetry.ts` does
 * for `libraryV2`.
 *
 * Both sides have to be readable in one console session, because the questions
 * this POC keeps running into are comparisons rather than lookups: which seats
 * the graph did not return (FINDINGS.md gap 9), which titles the window index
 * could not match, which `distributionAction` values arrive at all. A seat that
 * is only visible in the network tab as one row of a 68-row JSON body does not
 * answer any of those.
 *
 * Remove with the rest of the prototype's telemetry.
 */

/** `license` is the only nested object on the row; flattened so the table has real columns. */
const flattenSeat = (node: EntitlementNode): Record<string, unknown> => ({
  id: node.id,
  itemNumber: node.itemNumber,
  title: node.title,
  category: node.category,
  viewOn: node.viewOn,
  viewUntil: node.viewUntil,
  orderedOn: node.orderedOn,
  channels: node.distributionChannelCodes?.join(" ") ?? "",
  "license.id": node.license?.id,
  "license.seatCount": node.license?.seatCount,
  "license.isOwner": node.license?.isOwner,
  "license.isManager": node.license?.isManager,
})

const flattenItem = (item: EntitlementItem): Record<string, unknown> => ({
  id: item.id,
  itemNumber: item.itemNumber,
  title: item.title,
  category: item.category,
  state: item.window.state,
  viewOn: item.window.viewOn,
  viewUntil: item.window.viewUntil,
  accessLabel: item.accessLabel,
  accessHref: item.accessHref,
  manageHref: item.window.manageHref,
})

const countBy = <T>(values: readonly T[], key: (value: T) => string): Record<string, number> => {
  const counts: Record<string, number> = {}

  for (const value of values) {
    const bucket = key(value)
    counts[bucket] = (counts[bucket] ?? 0) + 1
  }

  return counts
}

export const logEntitlementSeats = (nodes: readonly EntitlementNode[], items: readonly EntitlementItem[]): void => {
  if (!import.meta.env.DEV) {
    return
  }

  console.groupCollapsed(`[entitlements] ${nodes.length} seat(s) from commerce`)

  if (nodes.length > 0) {
    console.table(nodes.map(flattenSeat))

    /**
     * The count per category is the number worth reading first: it is what tells
     * you 23 ebook seats arrived when the Ebooks section shows 15.
     */
    console.log(
      "by category:",
      countBy(nodes, node => String(node.category)),
    )
    console.log(
      "by access state:",
      countBy(items, item => item.window.state),
    )
    console.table(items.map(flattenItem))

    /**
     * `data` is the unmapped MLC row the fragment carries as POC scaffolding —
     * kept out of the tables, where it would be one unreadable column, and logged
     * whole so a field the schema drops is still findable.
     */
    console.log(
      "unmapped MLC rows:",
      nodes.map(node => node.data),
    )
  }

  console.log("raw seats:", nodes)
  console.log("projected rows:", items)
  console.groupEnd()
}
